// Phase-3 Slice 27 + 36 — Rename preview planner (facade).
//
// PRD: Ralph/NavigationalFeatures/PRD.md § Slice 27 (column) + § Slice 36
// (table/view).
//
// Builds a `RenameBatch` from a `RenameRequest` without executing any ALTER:
//   1. Synthesize the schema-side rename ALTER (`renameSchemaAlter.ts`):
//        - column → dialect.ts `synthesizeAlterRenameColumn`
//        - table/view → per-engine emitter `emitTableOrViewSchemaAlter`
//   2. Query the connection's IdentifierIndex for every reference to the
//      old identifier (`renameReferenceCollection.ts`); group records by
//      containing object (`refGrouper.ts`). For table/view rename we merge the
//      `'table'` and `'view'` buckets the same way `findReferencesService`
//      does — the index walker emits all relation refs under `identifierKind:
//      'table'` today, but a future walker change emitting `'view'` directly
//      stays compatible.
//   3. Per object: fetch the body; rewrite each ref's byte range to the new
//      name via `emitSource`; wrap the rewritten body in the dialect-native
//      ALTER through the per-engine emitter (`renameObjectBodyEdits.ts`).
//   4. Surface objects we couldn't rewrite (parse-failed body, missing body,
//      no per-dialect emitter, or unsupported emit op — e.g. SQLite
//      constraint rewrite) under `unrewritable[]` for the preview UI (also
//      `renameObjectBodyEdits.ts`).
//
// This file keeps the orchestration and the index-readiness handling; the three
// sibling modules above hold the steps. NAV-1 cross-table same-name
// disambiguation (a rename of `users.email` must not rewrite a body that
// references `orders.email`) lives with the matching it governs, in
// `renameReferenceCollection.ts`.
//
// Every rewrite touches only the TERMINAL component of the recorded reference
// and keeps its delimiters (`terminalTokenEdit.ts`): the index records whole
// `<alias>.<column>` chains, and replacing those wholesale dropped qualifiers
// and quoting.

import type { DatabaseEngineId } from '@/core/engines';

import { databaseEngineIdToSqlEngine } from '../../intellisense/versionGating/engineIdMapping';
import {
  fetchObjectBody as defaultFetchObjectBody,
  fetchProceduralObjectBody as defaultFetchProceduralObjectBody,
  type FetchObjectBodyOptions,
} from '../index/objectBodyFetcher';
import {
  getIdentifierIndexFor as defaultGetIdentifierIndexFor,
  indexReadyFor as defaultIndexReadyFor,
} from '../connection-lifecycle/navigationalLifecycle';
import type { MatchConfidence } from '../relationIdentity';
import type {
  IdentifierIndex,
  ReferenceRecord,
  RenameBatch,
  RenameRequest,
} from '../types';

import { getRenameEmitter as defaultGetEmitter, type DialectRenameEmitter } from './emit';
import { groupByObject } from './refGrouper';
import { resolveRenameDestination, resolveRestoreDestination } from './renameDestination';
import { planObjectBodyEdits } from './renameObjectBodyEdits';
import { collectRenameReferences } from './renameReferenceCollection';
import { planSchemaAlter } from './renameSchemaAlter';

export type PlanRenameDeps = {
  readonly getIndex?: (connectionId: string, database?: string) => IdentifierIndex | null;
  /** Wait for (building on first use) the identifier index of the rename's
   *  database — the lifecycle keeps one per (connection, database). Defaults
   *  to `indexReadyFor`; a rejection of ANY reason (no lifecycle entry, an
   *  unknown case policy, or a build CANCELLED by a disconnect/reconnect) means
   *  this wait's index never finished, so the plan bails to `{ status:
   *  'no-index' }` WITHOUT consulting `getIndex` — which could otherwise answer
   *  with a replacement index that is still building. */
  readonly awaitIndex?: (connectionId: string, database: string) => Promise<void>;
  readonly fetchObjectBody?: (options: FetchObjectBodyOptions) => Promise<string | null>;
  /** Procedural bodies (procedure / trigger) are fetched via this sibling
   *  feed — `fetchObjectBody` hard-returns null for procedural kinds, mirroring
   *  the split the index-builder relies on. */
  readonly fetchProceduralObjectBody?: (
    options: FetchObjectBodyOptions,
  ) => Promise<string | null>;
  readonly getEmitter?: (engineId: DatabaseEngineId) => DialectRenameEmitter | null;
};

export type PlanRenameInput = {
  readonly connectionId: string;
  readonly database: string;
  readonly request: RenameRequest;
  /** #34 opt-in: when true, unqualified/ambiguous relation-reference rewrites
   *  are INCLUDED in `objectEdits` (and therefore apply). Default (false)
   *  excludes them — they are surfaced in the preview behind a checkbox. */
  readonly includeAmbiguous?: boolean;
  /** MySQL `@@lower_case_table_names` (0/1/2) — thread the probed value from
   *  `mysqlSchemaIntrospection.ts` when available. Undefined → case-insensitive
   *  identity matching (the managed-MySQL / macOS / Windows default). */
  readonly mysqlLowerCaseTableNames?: number;
  /** MSSQL catalog collation — true = the database tells `dbo.V` from `dbo.v`.
   *  Omitted (the normal case) → derived from the index handle the wait below
   *  produced, which carries the collation the lifecycle probed for THIS
   *  database. Pass it only to override that. */
  readonly mssqlCatalogCaseSensitive?: boolean;
  /** MSSQL SERVER (instance) collation — true = the instance tells the
   *  database `Sales` from the database `sales`. A DIFFERENT collation from
   *  the one above, which governs object/schema names inside one database.
   *  Omitted (the normal case) → read from the connection's probed instance
   *  collation (`mssqlCollationProbe.ts`). Pass it only to override that. */
  readonly mssqlDatabaseNamesCaseSensitive?: boolean;
};

export type PlanRenameResult =
  | { readonly status: 'ok'; readonly batch: RenameBatch }
  | { readonly status: 'no-index' }
  | { readonly status: 'unsupported-engine' }
  | { readonly status: 'unsupported-kind'; readonly reason: string }
  | { readonly status: 'missing-table-context' }
  | { readonly status: 'missing-emitter' };

export async function planRename(
  input: PlanRenameInput,
  deps?: PlanRenameDeps,
): Promise<PlanRenameResult> {
  const { connectionId, database, request } = input;
  const sqlEngine = databaseEngineIdToSqlEngine(request.engineId);
  if (!sqlEngine) return { status: 'unsupported-engine' };
  if (request.kind === 'column' && !request.containingTable) {
    return { status: 'missing-table-context' };
  }

  const getIndex = deps?.getIndex ?? defaultGetIdentifierIndexFor;
  const awaitIndex = deps?.awaitIndex ?? defaultIndexReadyFor;
  const fetchBody = deps?.fetchObjectBody ?? defaultFetchObjectBody;
  const fetchProceduralBody =
    deps?.fetchProceduralObjectBody ?? defaultFetchProceduralObjectBody;
  const getEmitter = deps?.getEmitter ?? defaultGetEmitter;

  // The index for THIS database — built on demand if the rename targets a
  // database the connection was not configured for, kicked past the deferred
  // connect-time schedule otherwise. A rejection of ANY reason (no lifecycle,
  // policy unknown, or a build CANCELLED by a disconnect/reconnect mid-index)
  // means the index this wait was for never finished — reading `getIndex`
  // anyway would risk planning against a replacement index that is still
  // building (and would silently omit dependent-object rewrites), so the plan
  // bails out to `no-index` instead of consuming whatever `getIndex` returns.
  let awaitFailed = false;
  await awaitIndex(connectionId, database).catch(() => {
    awaitFailed = true;
  });
  if (awaitFailed) return { status: 'no-index' };
  const index = getIndex(connectionId, database);
  if (!index) return { status: 'no-index' };

  const emitter = getEmitter(request.engineId);

  // WHAT IDENTITY EACH DESTINATION HAS — decided ONCE, here, and CARRIED into
  // every step below and onto the batch. The delimiters a user may have typed
  // around the new name come off (so the ALTER delimits it once instead of
  // twice), and the spelling the catalog will actually hold is derived from the
  // statement step 1 is about to emit.
  //
  // BOTH directions are settled here. The undo's destination — the original
  // object's catalog identity — is as knowable now as the forward one, and
  // leaving it to be re-derived later is what made a case-exact original fold
  // on the way back (PostgreSQL `Id` → `id`, Oracle `row_id` → `ROW_ID`) while
  // the undo ALTER restored the exact bytes. See `renameDestination.ts`.
  const destination = resolveRenameDestination(request, sqlEngine);
  const undoDestination = resolveRestoreDestination(request, sqlEngine);

  // Step 1 — the schema-side ALTER (forward + undo). `null` back means a
  // table/view rename on an engine with no per-dialect emitter.
  const schema = await planSchemaAlter({
    connectionId,
    database,
    request,
    destination,
    undoDestination,
    sqlEngine,
    emitter,
    fetchBody,
  });
  if (!schema) return { status: 'missing-emitter' };

  // Step 2 — every reference the rename touches, scored per reference.
  const confidenceByRef = new Map<ReferenceRecord, MatchConfidence>();
  const records = collectRenameReferences({
    connectionId,
    database,
    request,
    sqlEngine,
    index,
    caseOverrides: input,
    confidenceByRef,
  });

  // Group by the index's own object-key rule — asked of the INDEX, which
  // reports the policy it actually froze. Two objects the index keeps apart are
  // two bodies to rewrite, never one, and recomputing the rule from
  // `(connectionId, engineId, database)` got it wrong whenever `database` was
  // empty: the lifecycle resolves that to the connection's configured database
  // and files SQL Server's per-database collation under the resolved identity.
  const groups = groupByObject(records, index.objectKeyCaseFor);

  // Steps 3 + 4 — rewrite each referencing body, collect the ones we can't.
  const bodies = await planObjectBodyEdits({
    connectionId,
    database,
    engineId: request.engineId,
    sqlEngine,
    fromName: request.fromIdentifier.name,
    // The body rewrite spells the destination the way the schema ALTER planned
    // in step 1 actually creates it, which depends on the kind, on the host
    // relation's own quoting and on whether the user typed delimiters — all
    // already settled in `destination` (see `renameDestination.ts`).
    destination,
    emitter,
    groups,
    confidenceByRef,
    includeAmbiguous: input.includeAmbiguous ?? false,
    fetchBody,
    fetchProceduralBody,
  });

  return {
    status: 'ok',
    batch: {
      target: {
        fromIdentifier: request.fromIdentifier,
        // The CATALOG's destination identity, not the cursor's — and read off
        // `destination` rather than rebuilt, so the invariant on
        // `RenameBatch.target.toIdentifier` holds by construction. Consumers
        // that match open buffers have to name what the schema ALTER really
        // creates: PostgreSQL's `RENAME COLUMN id TO User_Id` makes `user_id`,
        // and a batch that retained `User_Id` undid the database while leaving
        // every buffer on `user_id`. The name the user TYPED stays on the
        // request, which is what the preview dialog shows.
        toIdentifier: destination.identifier,
        kind: request.kind,
        containingTable: request.containingTable,
      },
      schemaAlter: schema.schemaAlter,
      objectEdits: bodies.objectEdits,
      unrewritable: bodies.unrewritable,
      undoBatch: [schema.undoSchemaAlter, ...bodies.undoBody],
      destination,
      undoDestination,
      dialect: request.engineId,
      ambiguousMatchCount: bodies.ambiguousMatchCount,
    },
  };
}
